import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import {
  Table, Input, DatePicker, Select,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import {
  fetchMicroDiscrepancy,
} from "../../../../actions/micro";
import useAbortController from "../../../Common/hooks/useAbortController";
import { getUser } from "../../../../utils/helpers";
import "./MicroDiscrepancy.css";

const { RangePicker } = DatePicker;
const dateFormat = "MM/DD/YYYY";
const defaultDateRange = () => {
  const startDate = moment().subtract(1, "months").format(dateFormat);
  const endDate = moment().format(dateFormat);
  return [dayjs(startDate, dateFormat), dayjs(endDate, dateFormat)];
};

const MicroDiscrepancyTableRow = ({
  record,
  ...antdProps
}) => {
  if (!record) {
    return <tr {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }

  return (
    <>
      <tr
        {...antdProps} // eslint-disable-line
        className="MicroDiscrepancyTableRow"
      />
    </>
  );
};

const MicroDiscrepancyTableCell = ({
  record,
  dataIndex,
  ...antdProps
}) => {
  if (!record) {
    return <td {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }
  const childNode = record?.[dataIndex] || "-";
  return <td {...antdProps}>{childNode}</td>; // eslint-disable-line react/jsx-props-no-spreading
};

function MicroPending() {
  const columns = [
    {
      title: "WO",
      dataIndex: "wo_id",
      key: "wo_id",
      permission: "no-permission",
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      permission: "no-permission",
    },
    {
      title: "Analysis",
      dataIndex: "analysis",
      key: "analysis",
      permission: "no-permission",
    },
    {
      title: "Petrifilm",
      dataIndex: "petrifilm",
      key: "petrifilm",
      permission: "no-permission",
    },
    {
      title: "DF",
      dataIndex: "df_value",
      key: "df_value",
      permission: "no-permission",
    },
    {
      title: "Raw Count",
      dataIndex: "raw_counts",
      key: "raw_counts",
      permission: "no-permission",
    },
    {
      title: "DF x Raw Count",
      dataIndex: "calculated_result",
      key: "calculated_result",
      permission: "no-permission",
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      permission: "no-permission",
    },
  ];
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(defaultDateRange());
  const [selectedTat, setSelectedTat] = useState("");
  const [selectedVisibility, setSelectedVisibility] = useState("");
  const user = getUser();
  const defaultPageSize = 100;

  const dayList = [
    { value: "All" },
    { value: "1-day" },
    { value: "2-day" },
    { value: "3-day" },
    { value: "5-day" },
    { value: "8-day" },
  ];

  const visibilityOptions = [
    { value: "", label: "All" },
    { value: "0", label: "Show Visible Sample" },
    { value: "1", label: "Show Hidden Sample" },
  ];

  const formatDateRange = (input, format = "YYYY-MM-DD") => {
    if (!input || input.length < 2) return "";
    const startDate = moment(input[0].$d).format(format);
    const endDate = moment(input[1].$d).format(format);
    return `${startDate} - ${endDate}`;
  };
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: defaultPageSize,
    total: defaultPageSize,
    pageSizeOptions: ["10", "50", "100", "200"],
  });
  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
    }));
  };

  const { setNewAbortController } = useAbortController();

  const handleFetchMicroDiscrepancy = async (searchValue = null, resetPage = true) => {
    setLoading(true);
    const newAbortController = setNewAbortController();

    const formattedDateRange = formatDateRange(dateRange);
    const params = {
      search: searchValue !== null ? searchValue : search,
      date_range: formattedDateRange || "",
      tat: selectedTat || "",
      view_hidden_sample: user.permissionMap?.view_hidden_samples ? selectedVisibility : "0",
      match_filter: "mismatch",
    };

    const {
      success, message, result, canceled,
    } = await fetchMicroDiscrepancy(params, newAbortController.signal);
    if (success) {
      setData(() => {
        const newData = result.map((item) => ({ ...item, key: uuidv4() }));
        // Update pagination config
        setPaginationConfig((prev) => {
          const newConfig = { ...prev };
          newConfig.total = newData.length;
          if (resetPage) {
            newConfig.current = 1;
          }
          return newConfig;
        });
        // Return new data
        return newData;
      });
    } else if (!canceled) {
      toast.error(message);
      setData([]);
      // Reset pagination on error
      setPaginationConfig((prev) => ({
        ...prev,
        total: 0,
        current: 1,
      }));
    }
    setLoading(canceled);
  };

  const onSearchChange = (value) => {
    setSearch(value);
    if (value === "") {
      // When cleared, immediately trigger API call with empty search and reset page
      handleFetchMicroDiscrepancy("", true);
    }
  };

  const handleOnSearch = (value) => {
    // onSearch receives the search value as parameter
    setSearch(value);
    handleFetchMicroDiscrepancy(value, true);
  };

  const handleDateChange = (value) => {
    const dateArray = value || defaultDateRange();
    setDateRange(dateArray);
  };

  const selectDayOption = (text) => {
    setSelectedTat(text === "All" ? "" : text);
  };

  const selectVisibilityOption = (text) => {
    setSelectedVisibility(text);
  };

  useEffect(() => {
    handleFetchMicroDiscrepancy(null, true);
  }, [dateRange, selectedTat, selectedVisibility]); // eslint-disable-line

  return (
    <div className="MicroDiscrepancy">
      <div className="MicroDiscrepancy__FilterContainer">
        <div className="dropdownSHFTContainer">
          <div className="datePickerLabel">
            Search
          </div>
          <Input.Search
            allowClear
            placeholder="Search by WO, Genista ID"
            className="MicroDiscrepancy__FilterSearch"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            onSearch={handleOnSearch}
          />
        </div>
        <div className="dropdownSHFTContainer">
          <div className="datePickerLabel">
            Date Range
          </div>
          <RangePicker
            allowClear={false}
            className="MicroDiscrepancy__FilterDate"
            format="MM/DD/YYYY"
            value={dateRange}
            onChange={handleDateChange}
          />
        </div>
        <div className="dropdownSHFTContainer">
          <div className="datePickerLabel">
            Filter by Day
          </div>
          <Select
            className="MicroGenerate__Select"
            popupClassName="MicroGenerate__Select__Menu"
            options={dayList}
            value={selectedTat === "" ? "All" : selectedTat}
            onSelect={(text) => selectDayOption(text)}
          />
        </div>
        {user.permissionMap?.view_hidden_samples && (
          <div className="dropdownSHFTContainer">
            <div className="datePickerLabel">
              Sample Visibility
            </div>
            <Select
              className="MicroGenerate__Select"
              popupClassName="MicroGenerate__Select__Menu"
              options={visibilityOptions}
              value={selectedVisibility}
              onSelect={(text) => selectVisibilityOption(text)}
            />
          </div>
        )}
      </div>
      <Table
        sticky
        className="QCTable"
        dataSource={data}
        scroll={{ y: "60vh" }}
        pagination={{ ...paginationConfig, onChange: handlePageChange }}
        loading={loading}
        components={{
          body: {
            row: MicroDiscrepancyTableRow,
            cell: MicroDiscrepancyTableCell,
          },
        }}
        onRow={(record) => ({ record })}
        columns={columns.map((col) => ({
          ...col,
          onCell: (record) => ({
            record,
            dataIndex: col.dataIndex,
          }),
        }))}
      />
    </div>
  );
}

export default MicroPending;
